<!-- Copyright (c) 2023 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="eInvoice-Main">
    <div class="menuSideBar">
      <Menus :menu="menuItems" @menuClicked="menuClicked" />
    </div>

    <div class="mainContent">
      <div class="filterBar">
        <div class="filter-selection">
          <div class="filter-sendto">
            <Dropdown
              placeholder="Send To"
              :items="sendToListFilter"
              itemKey="SendTo"
              :selectedObject.sync="selectedSendTo"
              :isRequired="true"
            />
          </div>
          <div class="filter-documenttype">
            <Dropdown
              v-if="showDocumentType"
              placeholder="Document Type"
              :items="documentTypeList"
              itemKey="doctype"
              :selectedObject.sync="selectedDocumentType"
              :isRequired="true"
              :errorMessage.sync="documentTypeError"
            />
          </div>
        </div>
        <div class="filter-status">
          <div class="statusFilterContainer">
            <div
              class="statusFilterButton"
              :style="{ 'background-color': showPending ? 'rgb(255, 255, 153)' : 'rgb(136, 136, 136)' }"
              @click="toggleStatus('pending')"
            >
              <div class="statusIcon"
                ><img
                  :src="
                    showPending ?
                      'https://dsmlib.logistics.systems/Icons/ClockBlack.png'
                    : 'https://dsmlib.logistics.systems/Icons/ClockWhite.png'
                  "
              /></div>
              <div
                class="statusLabel"
                :style="{
                  'text-decoration': showPending ? 'initial' : 'line-through',
                  color: showPending ? 'black' : 'white',
                }"
                >Pending</div
              >
              <div
                class="statusCounter"
                :style="{
                  'text-decoration': showPending ? 'initial' : 'line-through',
                  color: showPending ? 'black' : 'white',
                }"
                >{{ pendingeInvCount }}</div
              >
            </div>
            <div
              class="statusFilterButton"
              :style="{ 'background-color': showSent ? 'rgb(231, 150, 3)' : 'rgb(136, 136, 136)' }"
              @click="toggleStatus('sent')"
            >
              <div class="statusIcon"
                ><img
                  :src="
                    showSent ?
                      'https://dsmlib.logistics.systems/Icons/SendBlack.png'
                    : 'https://dsmlib.logistics.systems/Icons/SendWhite.png'
                  "
              /></div>
              <div
                class="statusLabel"
                :style="{
                  'text-decoration': showSent ? 'initial' : 'line-through',
                  color: showSent ? 'black' : 'white',
                }"
                >Sent</div
              >
              <div
                class="statusCounter"
                :style="{
                  'text-decoration': showSent ? 'initial' : 'line-through',
                  color: showSent ? 'black' : 'white',
                }"
                >{{ senteInvCount }}</div
              >
            </div>
            <div
              class="statusFilterButton"
              :style="{ 'background-color': showAccepted ? 'rgb(121, 210, 121)' : 'rgb(136, 136, 136)' }"
              @click="toggleStatus('accepted')"
            >
              <div class="statusIcon"
                ><img
                  :src="
                    showAccepted ?
                      'https://dsmlib.logistics.systems/Icons/CheckmarkBlack.png'
                    : 'https://dsmlib.logistics.systems/Icons/CheckmarkWhite.png'
                  "
              /></div>
              <div
                class="statusLabel"
                :style="{
                  'text-decoration': showAccepted ? 'initial' : 'line-through',
                  color: showAccepted ? 'black' : 'white',
                }"
                >Accepted</div
              >
              <div
                class="statusCounter"
                :style="{
                  'text-decoration': showAccepted ? 'initial' : 'line-through',
                  color: showAccepted ? 'black' : 'white',
                }"
                >{{ acceptedeInvCount }}</div
              >
            </div>
            <div
              class="statusFilterButton"
              :style="{ 'background-color': showRejected ? 'rgb(255, 77, 77)' : 'rgb(136, 136, 136)' }"
              @click="toggleStatus('rejected')"
            >
              <div class="statusIcon"
                ><img
                  :src="
                    showRejected ?
                      'https://dsmlib.logistics.systems/Icons/CrossmarkBlack.png'
                    : 'https://dsmlib.logistics.systems/Icons/CrossmarkWhite.png'
                  "
              /></div>
              <div
                class="statusLabel"
                :style="{
                  'text-decoration': showRejected ? 'initial' : 'line-through',
                  color: showRejected ? 'black' : 'white',
                }"
                >Rejected</div
              >
              <div
                class="statusCounter"
                :style="{
                  'text-decoration': showRejected ? 'initial' : 'line-through',
                  color: showRejected ? 'black' : 'white',
                }"
                >{{ rejectedeInvCount }}</div
              >
            </div>
          </div>
          <div class="generalFilterContainer">
            <div class="generalFilterContent" style="text-align: right; white-space: pre-line">{{
              filtersSummary
            }}</div>
            <div class="generalFilterButton">
              <ButtonFilter
                buttonText="Change Filter"
                :buttonIsDisabled="filterButtonDisabled"
                filterPosition="right"
                :filterWidth="350"
              >
                <BoxContainer title="Invoice Date" :isCheckBox="true" :isChecked.sync="dateFilterChecked">
                  <div style="display: flex; justify-content: space-between">
                    <DateTimePicker
                      placeholder="Start"
                      :showTime="false"
                      :value.sync="dateFilterStart"
                      :maxDate="dateFilterEnd"
                      :isDisabled="!dateFilterChecked"
                    />
                    <DateTimePicker
                      placeholder="End"
                      :showTime="false"
                      :value.sync="dateFilterEnd"
                      :minDate="dateFilterStart"
                      :isDisabled="!dateFilterChecked"
                    />
                  </div>
                </BoxContainer>
                <BoxContainer
                  title="Submission Date"
                  :isCheckBox="true"
                  :isChecked.sync="submissionDateFilterChecked"
                  :checkBoxIsDisabled.sync="submissionDateFilterDisabled"
                >
                  <div style="display: flex; justify-content: space-between">
                    <DateTimePicker
                      placeholder="Start"
                      :showTime="false"
                      :value.sync="submissionDateFilterStart"
                      :maxDate="submissionDateFilterEnd"
                      :isDisabled="!submissionDateFilterChecked"
                    />
                    <DateTimePicker
                      placeholder="End"
                      :showTime="false"
                      :value.sync="submissionDateFilterEnd"
                      :minDate="submissionDateFilterStart"
                      :isDisabled="!submissionDateFilterChecked"
                    />
                  </div>
                </BoxContainer>
                <BoxContainer title="Search" :isCheckBox="true" :checkBoxIsDisabled="true">
                  <div style="flex-direction: column">
                    <Dropdown placeholder="Search By" :isDisabled="!searchFilterChecked" />
                    <Dropdown placeholder="Search Criteria" :isDisabled="!searchFilterChecked" />
                    <Input placeholder="Search" :isDisabled="!searchFilterChecked" />
                  </div>
                </BoxContainer>
                <div class="changeFilterPopupRefreshButton">
                  <button @click="refreshClicked">Refresh</button>
                </div>
              </ButtonFilter>
            </div>
          </div>
        </div>
      </div>
      <div class="eInvoiceTableBoxContainer">
        <TableBox
          v-if="showeInvoiceList"
          title="eInvoices"
          :tableData.sync="eInvoiceListTableData"
          :selectedRow.sync="selectedInvoice"
          :additionalIconsIndex="[0]"
          :hideColumns="['Status', 'SubmissionUID']"
          :maxSelection="100"
          :dataType="eInvoiceListTableDataType"
        />
      </div>
    </div>

    <div v-if="showConfirmationPopup" class="confirmationPopupContainer">
      <div class="confirmationPopup">
        <table class="summaryHeader">
          <tr>
            <td>To</td>
            <td>:</td>
            <td>{{ selectedSendTo['SendTo'] }}</td>
          </tr>
          <tr>
            <td>Total Invoice</td>
            <td>:</td>
            <td>{{ selectedInvoice.length }}</td>
          </tr>
          <tr>
            <td>eInvoice(s)</td>
            <td>:</td>
            <td>{{ selectedeInvList }}</td>
          </tr>
        </table>

        <div class="invDetailsContainer">
          <div v-for="(eInv, index) of selectedInvoice" :key="eInv['Invoice #']" class="invDetails">
            <div class="invDetailsHeader">
              <table>
                <tr>
                  <td>Invoice #</td>
                  <td>:</td>
                  <td>{{ eInv['Invoice #'] }}</td>
                </tr>
                <tr>
                  <td>Invoice Date</td>
                  <td>:</td>
                  <td>{{ eInv['Invoice Date'] }}</td>
                </tr>
                <tr>
                  <td>Bill To</td>
                  <td>:</td>
                  <td>{{ eInv['Bill To'] }}</td>
                </tr>
              </table>
            </div>

            <hr v-if="index + 1 < selectedInvoice.length" class="invDetailsSeparator" />
            <!-- dont render if it is the last selectedeInv -->
          </div>
        </div>

        <div class="sendCancelButtonsContainer">
          <div class="sendCancelButton">
            <button @click="sendeInvoice">Send</button>
          </div>
          <div class="sendCancelButton">
            <button @click="showConfirmationPopup = false">Cancel</button>
          </div>
        </div>

        <!-- <div>

        </div> -->
      </div>
    </div>

    <PopupV2
      :show="showCancelPopup"
      title="Cancel eInvoice"
      @buttonClicked="cancelPopupButtonClicked"
      :buttons="cancelPopupButtons"
    >
      <div
        >Are you sure you want to cancel eInvoice submission for Invoice #{{
          selectedInvoice.length > 0 ? selectedInvoice[0]['Invoice #'] : ''
        }}?</div
      >
      <!-- <div v-if="selectedInvoice.length > 0 ? selectedJob[0]['Job Assigned'] > 0 : false"><br>Consignment under this Job ('{{ selectedJob.length > 0 ? selectedJob[0]['Job No'] : '' }}') has been assigned. Continue to delete?</div> -->

      <Input
        placeholder="Reason"
        :inputValue.sync="cancelReason"
        :errorMessage.sync="cancelReasonError"
        :isRequired="true"
      />
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    sendToListFilter: function () {
      return this.sendToList.filter((f) => f['hide'] == 'False');
    },
    selectedeInvList: function () {
      //generate a string containing selected eInv numbers seperated by commas
      if (this.selectedInvoice.length == 0)
        //eg. ABC23-01, ABC24-01
        return '';

      let list = this.selectedInvoice[0]['Invoice #'];
      for (let i = 1; i < this.selectedInvoice.length; i++) {
        list += `, ${this.selectedInvoice[i]['Invoice #']}`;
      }
      return list;
    },
  },
  data: function () {
    return {
      menuItems: [{ title: 'Send eInvoice', icon: 'https://dsmlib.logistics.systems/Icons/SendBlack.png' }],

      //flags for the status filter buttons at the top
      //styling for each of the buttons react to the state of their corresponding flag
      showPending: true,
      showSent: false,
      showAccepted: false,
      showRejected: false,

      //counter variables for status buttons at the top
      pendingeInvCount: 0,
      senteInvCount: 0,
      acceptedeInvCount: 0,
      rejectedeInvCount: 0,

      filtersSummary: '',
      showChangeFilterPopup: false,
      dateFilterChecked: false,
      dateFilterStart: null,
      dateFilterEnd: null,
      // lhdnControlDate: 3, // Settings system LHDN_ControlDate, default to 3 days
      submissionDateFilterDisabled: false,
      submissionDateFilterChecked: false,
      submissionDateFilterStart: null,
      submissionDateFilterEnd: null,
      searchFilterChecked: false,

      sendToList: [
        { SendTo: 'ELECTROLUX', key: 'SendeInvoice_ELECTROLUX', hide: 'True' },
        { SendTo: 'LHDN', key: 'SendeInvoice_LHDN', hide: 'True' },
        { SendTo: 'SAP', key: 'SendeInvoice_SAP', hide: 'True' },
      ],
      selectedSendTo: null,
      showDocumentType: false,
      documentTypeList: [
        { doctype: 'INVOICE', doctypecode: 'I' },
        { doctype: 'DEBIT NOTE', doctypecode: 'D' },
        { doctype: 'CREDIT NOTE', doctypecode: 'C' },
        { doctype: 'REFUND NOTE', doctypecode: 'R' },
        { doctype: 'SELF-BILLED INVOICE', doctypecode: 'SBI' },
        // { doctype: 'SELF-BILLED DEBIT NOTE', doctypecode: 'SBD'},
        // { doctype: 'SELF-BILLED CREDIT NOTE', doctypecode: 'SBC'},
        { doctype: 'SELF-BILLED REFUND NOTE', doctypecode: 'SBR'},
      ],
      selectedDocumentType: null,
      documentTypeError: '',
      filterButtonDisabled: true,
      eInvoiceList: [], //contains all eInvs returned by the API, including all statuses, appended with status icons
      showeInvoiceList: true,
      eInvoiceListTableData: [], //bound to main TableBox, contains eInvs to be shown
      eInvoiceListTableDataType: {
        'Invoice Date': 'date',
        'Total Amt': 'currency',
        'Total Tax Amt': 'currency',
        'Sent Time': 'datetime'
      },
      selectedInvoice: [],
      allowResent: false,

      showConfirmationPopup: false,
      showCancelPopup: false,
      cancelPopupButtons: [
        { text: 'Cancel', color: 'red', textColor: 'white' },
        { text: 'Back', color: 'lightgray', textColor: 'black' },
      ],
      cancelReason: '',
      cancelReasonError: '',
    };
  },
  props: {},
  methods: {
    menuClicked: async function (menuItem) {
      switch (menuItem.title) {
        case 'Send eInvoice':
          if (this.selectedInvoice.length == 0) {
            this.$showAlert('error', 'No Invoices Selected', 'Please select at least one Invoice to send');
            break;
          } else if (this.selectedInvoice.some((f) => f['Authorized'] === 'U')) {
            this.$showAlert('error', 'Unauthorized Invoice Selected', 'Please select only authorized Invoice');
            break;
          }

          //more checking based on selected send to
          if (this.selectedSendTo['SendTo'] == 'LHDN') {
            if (
              this.selectedInvoice.some(
                (f) => f['Status'] == 'Queue' || f['Status'] == 'Sent' || f['Status'] == 'Accepted',
              )
            ) {
              this.$showAlert(
                'error',
                'Send Invoices Selected',
                'Only Invoices in Pending, Rejected, Cancelled Status can be send',
              );
              break;
            }
            if (!this.selectedDocumentType) {
              this.documentTypeError = 'Please select a Document Type';
              break;
            }
          } else if (this.selectedSendTo['SendTo'] == 'ELECTROLUX') {
            if (
              this.selectedInvoice.some(
                (f) => f['Total Amt'] <= 0 || !f['BL #'] || f['BL #'] == '' || f['Containers'] == 0,
              )
            ) {
              this.$showAlert(
                'error',
                'Invoice Selected',
                'Invoice selected have missing mandatory fields.\nMust have (BL #, 1 or more Containers and not zero Total Amt)\nPlease add in the mandatory fields and refresh the records',
              );
              break;
            }

            const filter = this.selectedInvoice.filter((f) => f['Status'] == 'Sent');
            if (filter.length > 0 && this.allowResent == false) {
              //need to have checking to see how to handle only authorized user can send
              this.$showAlert('error', 'Send Invoices Selected', 'Only Authorized user can re-send Invoice');
              break;
            }
          }
          //not sure need to block rejected eInvoice or not. eROT blocked Rejected eROT to be send.
          this.showConfirmationPopup = true;
          break;
        case 'Cancel eInvoice':
          //should be only LDHN Send to have this option.
          if (this.selectedInvoice.length > 1) {
            this.$showAlert('error', 'Cancel eInvoice', 'Please select only 1 record to cancel');
            break;
          } else if (this.selectedInvoice.length == 0) {
            this.$showAlert('error', 'Cancel eInvoice', 'Please select 1 record to cancel');
            break;
          } else if (
            this.selectedInvoice.some(
              (f) =>
                f['Status'] == 'Queue' ||
                f['Status'] == 'Cancelled' ||
                f['Status'] == 'Rejected' ||
                f['Status'] == 'Pending',
            )
          ) {
            this.$showAlert('error', 'Cancel eInvoice', 'Only Invoices in "Sent" or "Accepted" status can be send');
            break;
          }

          this.showCancelPopup = true;

          break;
        case 'Retrieve eInvoice Status':
          await this.getAllDocumentDetails();
          break;
        case 'Print Document': {
          let printDocumentURL = '';

          if (this.selectedInvoice.length == 1) {
            if (this.selectedDocumentType['doctype'] == 'INVOICE') {
              printDocumentURL = `/Home/${this.$route.params.user}/FINAR/Print Invoice?invno=${this.selectedInvoice[0]['Invoice #']}`;
            } else if (this.selectedDocumentType['doctype'] == 'DEBIT NOTE') {
              printDocumentURL = `/Home/${this.$route.params.user}/FINAR/Print Debit Note?dnno=${this.selectedInvoice[0]['Invoice #']}`;
            } else if (this.selectedDocumentType['doctype'] == 'CREDIT NOTE') {
              printDocumentURL = `/Home/${this.$route.params.user}/FINAR/Print Credit Note?cnno=${this.selectedInvoice[0]['Invoice #']}`;
            } else if (this.selectedDocumentType['doctype'] == 'SELF-BILLED INVOICE') {
              printDocumentURL = `/Home/${this.$route.params.user}/FINPayment/Print Payment Voucher?payno=${this.selectedInvoice[0]['Invoice #']}`;
            } else {
              this.$showAlert(
                'error',
                'Print Selected',
                `Document ${this.selectedDocumentType['doctype']} cannot be printed`,
              );
              break;
            }
          } else {
            this.$showAlert('error', 'Print Selected', 'Only can select 1 document to print');
            break;
          }

          if (printDocumentURL) {
            const resolvedPrintDocumentRoute = this.$router.resolve(printDocumentURL);
            window.open(resolvedPrintDocumentRoute.href, '_blank'); // open in a new tab
          }
          break;
        }
      }
    },

    refreshClicked: async function () {
      if (this.selectedSendTo) {
        this.showChangeFilterPopup = false;
        this.$fullscreenLoading('Getting Data');

        if (this.selectedSendTo['SendTo'] == 'LHDN' && !this.selectedDocumentType) {
          this.documentTypeError = 'Please select a document type';
          this.$fullscreenLoading('');
          return;
        }

        if (!this.dateFilterChecked && !this.submissionDateFilterChecked) {
          this.$showAlert('warning', 'Filter Date', 'Must filter by either Invoice Date or Submission Date or both. ');
          this.$fullscreenLoading('');
          return;
        }

        this.filtersSummary = 'Filter by';
        if (this.dateFilterChecked && (this.dateFilterStart || this.dateFilterEnd)) {
          this.filtersSummary = `${this.filtersSummary} invoice date${this.dateFilterStart ? ` from ${this.dateFilterStart?.formatString('yyyy-MM-dd')}` : ''}${this.dateFilterEnd ? ` to ${this.dateFilterEnd?.formatString('yyyy-MM-dd')}` : ''}\n`;
        }

        if (this.submissionDateFilterChecked && (this.submissionDateFilterStart || this.submissionDateFilterEnd)) {
          this.filtersSummary = `${this.filtersSummary} submission date${this.submissionDateFilterStart ? ` from ${this.submissionDateFilterStart?.formatString('yyyy-MM-dd')}` : ''}${this.submissionDateFilterEnd ? ` to ${this.submissionDateFilterEnd?.formatString('yyyy-MM-dd')}` : ''}\n`;
        }

        const jsonBody = {
          sendtocode: this.selectedSendTo['SendTo'] ?? '',
          startinvdate: this.dateFilterChecked ? (this.dateFilterStart?.formatString('yyyy-MM-dd') ?? '') : '',
          endinvdate: this.dateFilterChecked ? (this.dateFilterEnd?.formatString('yyyy-MM-dd') ?? '') : '',
          doctype: this.selectedDocumentType ? this.selectedDocumentType['doctype'] : '',
          startsentdate:
            this.submissionDateFilterChecked ? (this.submissionDateFilterStart?.formatString('yyyy-MM-dd') ?? '') : '',
          endsentdate:
            this.submissionDateFilterChecked ? (this.submissionDateFilterEnd?.formatString('yyyy-MM-dd') ?? '') : '',
        };
        this.eInvoiceList = await this.$apiCallV2('/eInvoice/GeteARInvoice', jsonBody);

        if (this.eInvoiceList?.[0]?.['Results'] == '1') {
          this.$showAlert('error', 'Refresh Data', this.eInvoiceList[0]['ErrMsg']);
          this.selectedInvoice = []; //clear selectedeInoivce array
          this.eInvoiceListTableData = []; //clear TableBox data
        } else {
          this.eInvoiceList = this.eInvoiceList.map((eInv) => {
            //append status icons
            switch (eInv['Status']) {
              case 'Pending':
                return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/ClockBlack.png' }, eInv);
              case 'Sent':
                return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/SendBlack.png' }, eInv);
              case 'Accepted':
                return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusComplete.png' }, eInv);
              case 'Rejected':
                return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusCancelled.png' }, eInv);
            }
          });
          this.selectedInvoice = []; //clear selectedeInoivce array
          this.eInvoiceListTableData = []; //clear TableBox data
          this.$nextTick(() => {
            this.eInvoiceListTableData = this.filterStatus(); //filter out eInoivces with statuses hidden by the user
          });

          this.updateStatusCounters();
        }

        this.$fullscreenLoading('');
      }
    },

    updateStatusCounters: function () {
      this.pendingeInvCount = this.eInvoiceList.filter((f) => f['Status'] === 'Pending').length;

      this.senteInvCount = this.eInvoiceList.filter((f) => f['Status'] === 'Sent').length;

      this.acceptedeInvCount = this.eInvoiceList.filter((f) => f['Status'] === 'Accepted').length;

      this.rejectedeInvCount = this.eInvoiceList.filter((f) => f['Status'] === 'Rejected').length;
    },

    toggleStatus: async function (status) {
      switch (status) {
        case 'pending':
          this.showPending = !this.showPending;
          break;
        case 'sent':
          this.showSent = !this.showSent;
          break;
        case 'accepted':
          this.showAccepted = !this.showAccepted;
          break;
        case 'rejected':
          this.showRejected = !this.showRejected;
          break;
      }

      this.showeInvoiceList = false;
      await this.$nextTick();
      this.eInvoiceListTableData = this.filterStatus(); //call filter sttaus to filter out eInvoices hidden by the user, assign returned value to TableBox data
      const eInvoicesTemp = this.selectedInvoice.map((m) => m['Invoice #']);
      this.selectedInvoice = this.eInvoiceListTableData.filter((f) => eInvoicesTemp.includes(f['Invoice #']));
      this.showeInvoiceList = true;
    },

    filterStatus: function () {
      let filtered = this.eInvoiceList; //copy over full lists of eInovices
      if (!this.showPending)
        filtered = filtered.filter((eInv) => {
          return eInv['Status'] != 'Pending';
        }); //filter out Pending eInvs

      if (!this.showSent)
        filtered = filtered.filter((eInv) => {
          return eInv['Status'] != 'Sent';
        }); //filter out Sent eInvs

      if (!this.showAccepted)
        filtered = filtered.filter((eInv) => {
          return eInv['Status'] != 'Accepted';
        }); //filter out Accepted eInvs

      if (!this.showRejected)
        filtered = filtered.filter((eInv) => {
          return eInv['Status'] != 'Rejected';
        }); //filter out Rejected eInvs

      return filtered;
    },

    sendeInvoice: async function () {
      //send eInvoice to different send to
      this.$fullscreenLoading('Sending Invoice');
      if (this.selectedSendTo['SendTo'] == 'ELECTROLUX') {
        //Electrolux send
        const invnoarray = this.selectedInvoice.map((m) => m['Invoice #']).join(',');

        const jsonBody = {
          invnoarray: invnoarray,
        };

        const result = await this.$apiCallV2('/eInvoice/SendElectroluxInvSFTP', jsonBody);
        if (result[0]['Results'] == '0') {
          this.$showAlert(
            'success',
            'Send eInvoice success',
            `Successfully sent ${this.selectedeInvList} to ${this.selectedSendTo['SendTo']}`,
          );
          this.showConfirmationPopup = false;
          this.refreshClicked();
        }
      } else if (this.selectedSendTo['SendTo'] == 'LHDN') {
        //LHDN send
        const invnoarr = [];

        this.selectedInvoice.forEach((f) => {
          invnoarr.push({
            invno: f['Invoice #'],
            //'doctype': this.selectedDocumentType['doctype']
            doctype: this.selectedDocumentType['doctypecode'],
          });
        });

        const jsonBody = {
          invnoarr: invnoarr,
        };

        const result = await this.$apiCallV2('/eInvoice/SubmiteInvoice', jsonBody);
        if (result?.[0]?.['Results'] == '0') {
          await this.$showAlert(
            'success',
            'Send Document Success',
            `Sucessfully send ${this.selectedeInvList} to ${this.selectedSendTo['SendTo']}`,
          );
        } else if (result?.[0]?.['Results'] == '2') {
          await this.$showAlert('warning', 'Send Document Partially Failed', `${result[0]['ErrMsg']}`);
        } else {
          await this.$showAlert(
            'error',
            'Send Document Failed',
            `Error sending ${this.selectedeInvList} to ${this.selectedSendTo['SendTo']}\n${result?.[0]?.['ErrMsg']}`,
          );
        }

        this.showConfirmationPopup = false;
        await this.refreshClicked();
        this.getAllDocumentDetails();
      }
      this.$fullscreenLoading('');
    },

    cancelPopupButtonClicked: async function (e) {
      if (e == 'Cancel') {
        if (!this.cancelReason) {
          this.cancelReasonError = 'Please enter your reason to cancel document';
          return;
        }

        this.$fullscreenLoading('Cancelling eInvoice');
        const invnoarr = [];
        this.selectedInvoice.forEach((f) => {
          invnoarr.push({
            invno: f['Invoice #'],
            uuid: f['UUID'],
            cancelreason: this.cancelReason,
          });
        });

        const jsonBody = {
          invnoarr: invnoarr,
          // env: 'SANDBOX'
        };

        const result = await this.$apiCallV2('/eInvoice/CanceleInvoice', jsonBody);
        if (result[0]['Results'] == '0') {
          await this.$showAlert('success', 'Cancel Document Success', 'Succesfully cancelled document');
          this.showCancelPopup = false;
          await this.refreshClicked();
          this.getAllDocumentDetails();
        } else {
          await this.$showAlert('error', 'Cancel Document Failed', `Error cancelling document\n${result[0]['ErrMsg']}`);
          this.showCancelPopup = false;
          await this.refreshClicked();
          this.getAllDocumentDetails();
        }
        this.$fullscreenLoading('');
      }

      this.cancelReason = '';
      this.cancelReasonError = '';
      this.showCancelPopup = false;
    },

    getAllDocumentDetails: async function () {
      this.$fullscreenLoading('Retrieving status');
      const uuidInvoice = this.eInvoiceList.filter((f) => f['UUID'] && f['Status'] == 'Sent');
      if (uuidInvoice.length > 0) {
        const invnoarr = [];
        const submissionuidset = new Set(); //set to save only distinct value
        uuidInvoice.forEach((f) => {
          invnoarr.push({
            invno: f['Invoice #'],
            uuid: f['UUID'],
          });
        });
        uuidInvoice.forEach((f) => {
          submissionuidset.add(f['SubmissionUID']);
        });

        const jsonBody = {
          submissionuidarr: Array.from(submissionuidset).join(','), //convert to array to enable join
        };

        const result = await this.$apiCallV2('/eInvoice/GetSubmission', jsonBody);
        if (result[0]['Results'] == '0') {
          this.$showAlert('success', 'Refresh Document Status', 'Document status retrieved. Document list refreshed');
          this.refreshClicked();
        } else {
          this.$showAlert('error', 'Refreh Document Status', `Error retrieving status. `);
        }
      } else {
        this.$showAlert('warning', 'Refresh Document Status', 'No sent document available to retrieve status');
      }
      this.$fullscreenLoading('');
    },
  },
  async mounted() {
    this.$fullscreenLoading('Getting Data');

    const sendToSAS = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=FINAR&page=ROLEMANAGEMENT`);

    this.sendToList.forEach((a) => {
      const find = sendToSAS.find((b) => a['key'] == b['Control']);
      if (find) {
        a['hide'] = find['Propertiesvalue'];
      }
    });

    const allowResent = sendToSAS.find((f) => f['Control'] == 'SendeInvoice_Resend');
    if (allowResent) {
      this.allowResent = allowResent['Propertiesvalue'] == 'False';
    }

    // const controldateSetting = (await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=LHDN_ControlDate'))[0];
    // this.lhdnControlDate = parseInt(controldateSetting['SettingsValue'] || 3);
    this.$fullscreenLoading('');

    this.$nextTick(() => {
      if (this.sendToListFilter.length == 1) {
        this.selectedSendTo = this.sendToListFilter[0];
      }
    });
  },
  watch: {
    selectedSendTo: function (val) {
      if (val) {
        this.filterButtonDisabled = false;
        this.submissionDateFilterDisabled = true;
        this.eInvoiceList = [];
        this.eInvoiceListTableData = [];
        this.documentTypeError = '';

        this.dateFilterChecked = true;
        const date = new Date();
        // const startDate = new Date().setDate(date.getDate() - this.lhdnControlDate);
        // this.dateFilterStart = new Date(startDate);
        this.dateFilterStart = new Date(date.getFullYear(), date.getMonth(), 1);
        this.dateFilterEnd = date;

        if (val['SendTo'] == 'LHDN') {
          this.menuItems = [
            { title: 'Send eInvoice', icon: 'https://dsmlib.logistics.systems/Icons/SendBlack.png' },
            { title: 'Cancel eInvoice', icon: 'https://dsmlib.logistics.systems/Icons/SendBlack.png' },
            { title: 'Retrieve eInvoice Status', icon: 'https://dsmlib.logistics.systems/Icons/SendBlack.png' },
            { title: 'Print Document', icon: 'https://dsmlib.logistics.systems/Icons/Print.png' },
          ];
          this.submissionDateFilterDisabled = false;
          this.showDocumentType = true;
          if (!this.selectedDocumentType) {
            this.selectedDocumentType = this.documentTypeList.getSelected('INVOICE', 'doctype');
          }
        }

        this.$nextTick(() => this.refreshClicked());
      } else {
        this.filterButtonDisabled = true;
        this.submissionDateFilterDisabled = true;
        this.eInvoiceList = [];
        this.eInvoiceListTableData = [];
        this.selectedInvoice = [];
        this.documentTypeError = '';
        this.pendingeInvCount = 0;
        this.senteInvCount = 0;
        this.acceptedeInvCount = 0;
        this.rejectedeInvCount = 0;

        this.menuItems = [{ title: 'Send eInvoice', icon: 'https://dsmlib.logistics.systems/Icons/SendBlack.png' }];
        this.showDocumentType = false;
      }
    },

    selectedDocumentType: function (val) {
      if (val) {
        this.filterButtonDisabled = false;
        this.$nextTick(() => this.refreshClicked());
      } else {
        this.filterButtonDisabled = true;
        this.eInvoiceList = [];
        this.eInvoiceListTableData = [];
        this.selectedInvoice = [];
        this.documentTypeError = '';
        this.pendingeInvCount = 0;
        this.senteInvCount = 0;
        this.acceptedeInvCount = 0;
        this.rejectedeInvCount = 0;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.eInvoice-Main {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  padding: 10px 45px;
  font-size: 12px;

  > .mainContent {
    width: 100%;
    height: 100%;
    border-radius: 5px;

    > .filterBar {
      background-color: white;
      width: 100%;
      height: 150px;
      display: flex;
      flex-direction: column;

      > .filter-selection {
        width: 100%;
        display: flex;
        flex-direction: row;

        > .filter-sendto {
          padding: 5px;
          width: 200px;
        }

        > .filter-documenttype {
          padding: 5px;
          width: 200px;
        }
      }

      > .filter-status {
        width: 100%;
        display: flex;
        flex-direction: row;

        > .statusFilterContainer {
          display: flex;
          flex-direction: row;
          margin: 5px 5px 5px 5px;
          justify-content: right;
          flex: 1.5;
        }

        > .generalFilterContainer {
          margin: auto 5px auto 5px;
          padding-right: 5px;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: right;
          flex: 1.6;

          > .generalFilterButton {
            display: flex;
            height: 100%;
            align-items: flex-end;
            justify-content: right;
            padding-bottom: 5px;
            padding-right: 30px;
          }
        }
      }
    }

    > .eInvoiceTableBoxContainer {
      background-color: white;
      // margin-top: 5px;
      width: 100%;
      padding: 5px;
      height: calc(100% - 155px);
    }
  }

  > .confirmationPopupContainer {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;

    > .confirmationPopup {
      background-color: white;
      width: 80%;
      height: 95%;
      position: absolute;
      padding: 5px;
      display: flex;
      flex-direction: column;

      > .summaryHeader {
        margin-bottom: 10px;
        display: block;
      }

      > .invDetailsContainer {
        margin-bottom: 10px;
        overflow-y: scroll;
        border: 1px sold black;

        > .invDetails {
          height: auto;

          > .invDetailsHeader {
            display: flex;
            flex-direction: row;
            border: 1px solid black;

            > .invDetailsHeaderPrintContainer {
              display: flex;
              justify-content: right;
              align-items: center;
              flex: 1;

              > .invDetailsHeaderPrintButton {
                margin-right: 10px;
              }
            }
          }

          .invAttachmentsContainer {
            display: flex;
            flex-direction: row;
            border: 1px solid black;

            > .invAttachmentsTableBox {
              flex: 5;
              overflow-x: hidden;
              border-bottom: 1px solid black;
              border-right: 1px solid black;
            }

            > .invAddAttachmentsContainer {
              flex: 2;
              margin: 40px 5px 10px 5px;
            }
          }
        }
      }
    }
  }
}

.changeFilterPopup {
  position: absolute;
  margin-top: 29px;
  height: fit-content;
  width: 320px;
  background-color: white;
  border: 1px solid black;
  border-radius: 5px;
  padding: 5px 10px;
  z-index: 99;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.changeFilterPopupItemContainer {
  position: relative;
  width: 100%;
}

.changeFilterPopupItemCheckBox {
  position: absolute;
  width: fit-content;
  top: -5px;
  left: 3%;
  padding: 0 10px;
  background-color: white;
}
</style>
